import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { ArrowRight } from "lucide-react";
import { type } from "@/lib/typography";
import { cn } from "@/lib/utils";

export type IntentArt = "customers" | "fundraising" | "team" | "catalog";

interface Props {
  to: string;
  label: string;
  description?: string;
  art: IntentArt;
  cta: string;
}

function Art({ kind }: { kind: IntentArt }) {
  const common = "absolute right-3 top-3 h-[112px] w-[112px] transition-transform duration-500 ease-out group-hover:scale-105";
  if (kind === "customers") {
    return (
      <svg viewBox="0 0 120 120" aria-hidden className={common}>
        {[40, 30, 20].map((r, i) => (
          <circle key={r} cx="60" cy="60" r={r} fill="none" stroke="hsl(var(--primary))" strokeOpacity={0.15 + i * 0.12} strokeWidth="1.5" className="motion-safe:animate-[pulse_3s_ease-in-out_infinite]" style={{ animationDelay: `${i * 0.4}s` }} />
        ))}
        <circle cx="60" cy="60" r="6" fill="hsl(var(--primary))" />
        <circle cx="22" cy="34" r="7" fill="hsl(var(--accent))" opacity="0.85" />
        <circle cx="100" cy="40" r="6" fill="hsl(var(--primary))" opacity="0.7" />
        <circle cx="30" cy="96" r="6" fill="hsl(var(--accent))" opacity="0.65" />
      </svg>
    );
  }
  if (kind === "fundraising") {
    return (
      <svg viewBox="0 0 120 120" aria-hidden className={common}>
        {[
          { x: 14, h: 28 },
          { x: 36, h: 50 },
          { x: 58, h: 72 },
          { x: 80, h: 90 },
        ].map((b, i) => (
          <rect key={i} x={b.x} y={108 - b.h} width="14" height={b.h} rx="3" fill="hsl(var(--primary))" opacity={0.35 + i * 0.18} />
        ))}
        <path d="M 18 80 L 44 60 L 70 44 L 96 24" stroke="hsl(var(--accent))" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <polygon points="96,24 88,28 92,34" fill="hsl(var(--accent))" />
      </svg>
    );
  }
  if (kind === "team") {
    const nodes: Array<[number, number]> = [[24, 30], [60, 60], [96, 30], [40, 92], [82, 92]];
    const edges: Array<[number, number]> = [[0, 1], [2, 1], [1, 3], [1, 4]];
    return (
      <svg viewBox="0 0 120 120" aria-hidden className={common}>
        {edges.map(([a, b], i) => (
          <line key={i} x1={nodes[a][0]} y1={nodes[a][1]} x2={nodes[b][0]} y2={nodes[b][1]} stroke="hsl(var(--primary))" strokeOpacity="0.4" strokeWidth="1.5" />
        ))}
        {nodes.map(([x, y], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="10" fill="hsl(var(--primary))" opacity={i === 1 ? 0.9 : 0.55} />
            <circle cx={x} cy={y} r="4" fill="hsl(var(--accent))" opacity={i === 1 ? 1 : 0.7} />
          </g>
        ))}
      </svg>
    );
  }
  // catalog
  return (
    <svg viewBox="0 0 120 120" aria-hidden className={common}>
      {[0, 1, 2].map((c) =>
        [0, 1].map((r) => {
          const highlight = c === 1 && r === 0;
          return (
            <rect
              key={`${c}-${r}`}
              x={10 + c * 36}
              y={18 + r * 46}
              width="30"
              height="38"
              rx="6"
              fill={highlight ? "hsl(var(--accent))" : "hsl(var(--primary))"}
              opacity={highlight ? 0.95 : 0.35 + ((c + r) % 2) * 0.15}
            />
          );
        }),
      )}
    </svg>
  );
}

export function IntentCard({ to, label, description, art, cta }: Props) {
  return (
    <Link
      to={to}
      className={cn(
        "group relative flex min-h-[200px] flex-col justify-between overflow-hidden rounded-2xl border border-border bg-card p-6 transition-all duration-base ease-brand",
        "hover:-translate-y-1 hover:border-primary/60 hover:shadow-glow-primary",
      )}
    >
      <Art kind={art} />
      <div className="relative z-10 max-w-[60%]">
        <h3 className="text-foreground" style={type.cardTitle}>{label}</h3>
        {description ? (
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{description}</p>
        ) : null}
      </div>
      <span className="relative z-10 inline-flex items-center gap-1 text-xs font-medium text-primary">
        {cta}
        <ArrowRight className="h-3 w-3 transition-transform duration-base group-hover:translate-x-1" />
      </span>
    </Link>
  );
}